import * as Sentry from "@sentry/react"
import { useCallback, useEffect, useMemo } from "react"

import type { LibraryResponses } from "@tfg/types/endpoints/library"

import { useFindFeatureFlagByName } from "~/hooks/useFindFeatureFlagByName"
import { isSupplier } from "~/utils/permissions"
import { useFindMyUserOnboardingState } from "~/gen/api/hooks/useFindMyUserOnboardingState"
import { ONBOARDING_FEATURE_FLAG_NAME } from "~/pages/SupplierPortal/Onboarding/constants/onboardingGate.constants"
import type { OnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/utils/onboardingEntryDecision"
import { isAuthError } from "~/pages/SupplierPortal/Onboarding/utils/isAuthError"
import { resolveOnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/utils/onboardingEntryDecision"
import type { GateFailure } from "~/pages/SupplierPortal/Onboarding/utils/resolveGateState"
import { GATE_FAILURE_SOURCE } from "~/pages/SupplierPortal/Onboarding/utils/resolveGateState"

const recordOnboardingGateOutcome = (
  decision: OnboardingEntryDecision
): void => {
  Sentry.addBreadcrumb({
    category: "onboarding.gate",
    message: `onboarding_gate_${decision.outcome}`,
    level: "info",
    data: {
      outcome: decision.outcome,
      stepId: decision.stepId,
    },
  })
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error("Onboarding gate request failed")

type UseOnboardingEntryDecisionResult = Readonly<{
  decision: OnboardingEntryDecision | null
  isLoading: boolean
  isAuthError: boolean
  authError: Error | null
  gateFailure: GateFailure | null
  retry: () => void
}>

export const useOnboardingEntryDecision = (
  user: LibraryResponses.TFGUser | null
): UseOnboardingEntryDecisionResult => {
  const isEligible = user !== null && isSupplier(user)

  const featureFlagQuery = useFindFeatureFlagByName(
    ONBOARDING_FEATURE_FLAG_NAME,
    {
      query: { enabled: isEligible },
    }
  )

  const isOnboardingFeatureEnabled =
    featureFlagQuery.isSuccess && featureFlagQuery.data?.data?.state === true

  const shouldFetchOnboarding = isEligible && isOnboardingFeatureEnabled

  const onboardingStateQuery = useFindMyUserOnboardingState({
    query: { enabled: shouldFetchOnboarding },
  })

  const featureFlagError = isEligible ? featureFlagQuery.error : null
  const onboardingStateError = shouldFetchOnboarding
    ? onboardingStateQuery.error
    : null
  const error = featureFlagError ?? onboardingStateError
  const authError =
    featureFlagError !== null && isAuthError(featureFlagError)
      ? toError(featureFlagError)
      : onboardingStateError !== null && isAuthError(onboardingStateError)
        ? toError(onboardingStateError)
        : null
  const isAuthFailure = authError !== null
  const gateFailure = useMemo((): GateFailure | null => {
    if (isAuthFailure) {
      return null
    }

    if (featureFlagError !== null) {
      return {
        source: GATE_FAILURE_SOURCE.FEATURE_FLAG,
        error: toError(featureFlagError),
      }
    }

    if (onboardingStateError !== null) {
      return {
        source: GATE_FAILURE_SOURCE.ONBOARDING_STATE,
        error: toError(onboardingStateError),
      }
    }

    return null
  }, [featureFlagError, isAuthFailure, onboardingStateError])
  const isLoading =
    (isEligible && featureFlagQuery.isPending) ||
    (shouldFetchOnboarding && onboardingStateQuery.isPending)

  const onboardingState = shouldFetchOnboarding
    ? (onboardingStateQuery.data?.data.state ?? null)
    : null

  const decision = useMemo(() => {
    if (isLoading || error !== null) return null

    return resolveOnboardingEntryDecision({
      isEligible,
      isOnboardingFeatureEnabled,
      state: onboardingState,
    })
  }, [
    error,
    isEligible,
    isLoading,
    isOnboardingFeatureEnabled,
    onboardingState,
  ])

  const retry = useCallback((): void => {
    if (featureFlagQuery.isError) {
      void featureFlagQuery.refetch()
    }

    if (onboardingStateQuery.isError) {
      void onboardingStateQuery.refetch()
    }
  }, [featureFlagQuery, onboardingStateQuery])

  useEffect(() => {
    if (decision === null || !shouldFetchOnboarding) return

    recordOnboardingGateOutcome(decision)
  }, [decision, shouldFetchOnboarding])

  return {
    decision,
    isLoading,
    isAuthError: isAuthFailure,
    authError,
    gateFailure,
    retry,
  }
}
